import {createFragmentContainer, graphql} from 'react-relay';
import React, {useEffect} from 'react';
import * as tracking from '../../../../tracking';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import {MODAL_TYPE, showModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {Button} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import ForecastTooltip from '../../../../forecast-app/shared/components/tooltips/ForecastTooltip';
import {EconomicIcon, QuickbooksIcon, SageIntacctIcon, XeroIcon} from '@/web-components';
import {
	hasFeatureFlag,
	isQboInvoicingImprovementsEnabled,
	isXeroInvoicingImprovementsEnabled,
} from '../../../../forecast-app/shared/util/FeatureUtil';
import {prefetchXeroLineConfigForCompany} from '../../../../containers/modal/export_invoice_modal/ExportInvoiceModal.xeroLines';

const sortClients = function (a, b) {
	const labelA = a.label.toUpperCase();
	const labelB = b.label.toUpperCase();
	if (labelA < labelB) {
		return -1;
	}
	if (labelA > labelB) {
		return 1;
	}
	return 0;
};

const onExportClick = (invoice, company) => {
	if (hasFeatureFlag('invoicing_pathways')) {
		showModal({
			type: MODAL_TYPE.EXPORT_INVOICE_BILLING_OPTION,
			invoiceId: invoice.id,
		});
	} else if (!invoice.xeroId && !invoice.quickbooksId && !invoice.economicId) {
		// Show invoice export modal

		// Xero
		if (company.newXeroEnabled) {
			tracking.trackEvent('Export to Xero pressed');
			trackEvent('Export To Xero', 'Clicked');
			const xeroClients = company.clients.edges
				.filter(edge => edge.node.xeroContactId)
				.map(edge => ({
					value: edge.node.id,
					label: edge.node.name,
					xeroContactId: edge.node.xeroContactId,
					// T4-197: in the page data only with the flag on (@include), so no key at all flag off.
					...('xeroDefaultTaxRate' in edge.node && {xeroDefaultTaxRate: edge.node.xeroDefaultTaxRate}),
				}))
				.sort(sortClients);
			/*
			 * The new-design window with a client-selection header, the same one the QuickBooks
			 * branch opens, so the Xero export defaults are built once. Flag off is today's window,
			 * untouched.
			 */
			showModal(
				isXeroInvoicingImprovementsEnabled()
					? {type: MODAL_TYPE.EXPORT_INVOICE, invoiceId: invoice.id, xeroClients}
					: {type: MODAL_TYPE.XERO_EXPORT, invoiceId: invoice.id, xeroClients}
			);
		}
		// Sage Intacct
		else if (company.sageIntacctConnectionEnabled) {
			tracking.trackEvent('Export to Sage Intacct pressed');
			trackEvent('Export To Sage Intacct', 'Clicked');
			showModal({
				type: MODAL_TYPE.SAGE_INTACCT_EXPORT,
				invoiceId: invoice.id,
				sageIntacctCustomers: company.clients.edges
					.filter(edge => edge.node.sageIntacctCustomerId)
					.map(edge => ({value: edge.node.sageIntacctCustomerId, label: edge.node.name}))
					.sort(sortClients),
			});
		}
		// E-conomic
		else if (company.economicEnabled) {
			tracking.trackEvent('Export to E-conomic pressed');
			trackEvent('Export To Economic', 'Clicked');
			showModal({
				type: MODAL_TYPE.ECONOMIC_EXPORT,
				invoiceId: invoice.id,
				economicCustomers: company.clients.edges
					.filter(edge => edge.node.economicCustomerId)
					.map(edge => ({value: edge.node.id, label: edge.node.name}))
					.sort(sortClients),
			});
		}
		// QuickBooks
		else if (company.quickbooksEnabled) {
			tracking.trackEvent('Export to QuickBooks pressed');
			trackEvent('Export To Quickbooks', 'Clicked');
			const quickbooksCustomers = company.clients.edges
				.filter(edge => edge.node.quickbooksCustomerId)
				.map(edge => ({
					value: edge.node.id,
					label: edge.node.name,
					realmId: edge.node.quickbooksRealmId,
					// T4-188: the client's stored default, for the window's header and lines. Undefined, not null,
					// when it has none, so options without one keep their shape.
					defaultTaxRate: edge.node.qboDefaultTaxRate ?? undefined,
				}))
				.sort(sortClients);
			/*
			 * T4-149: the new-design window with a client-selection header, so the export
			 * defaults (T4-150/151) are built once. Flag off is today's window, untouched.
			 */
			showModal({
				type: isQboInvoicingImprovementsEnabled() ? MODAL_TYPE.EXPORT_INVOICE : MODAL_TYPE.QUICKBOOKS_EXPORT,
				invoiceId: invoice.id,
				quickbooksCustomers,
			});
		}
	}
};
const ExportInvoiceButton = ({company, project, invoiceId, xeroTenantToShortCode, invoice}) => {
	const {formatMessage} = useIntl();

	/*
	 * Read the line configuration while the page renders, not when the modal opens.
	 *
	 * The modal cannot start any earlier than it does: it needs a Xero contact to know which
	 * organisation to read, and that is itself a request. Two round trips after the click is the
	 * "Loading..." in the dropdowns. Starting here turns it into nothing to wait for.
	 *
	 * Safe to run once per row: `prefetchXeroLineConfigForCompany` memoises the contacts read and
	 * skips organisations already cached, so a page of invoices makes one contacts call and one set
	 * of configuration calls per organisation, not one of each per row.
	 *
	 * Gated on the flag because nothing else reads this configuration: without it the table is not
	 * rendered and these would be requests for data no one displays.
	 */
	useEffect(() => {
		if (company?.newXeroEnabled && isXeroInvoicingImprovementsEnabled()) {
			void prefetchXeroLineConfigForCompany();
		}
	}, [company?.newXeroEnabled]);

	const projectInvoice = project?.invoices?.edges?.find(e => e?.node?.id === invoiceId)?.node;
	const {xeroId, xeroTenantId, quickbooksId, economicId, sageIntacctId, status, quickbooksRealmId} =
		projectInvoice || invoice;

	if (xeroId) {
		let xeroShortCode;
		if (xeroTenantToShortCode) {
			xeroShortCode = xeroTenantToShortCode[xeroTenantId];
		}

		return (
			<ForecastTooltip content={formatMessage({id: 'invoicing.open_invoice_in'}, {systemName: 'Xero'})} delay={[50, 100]}>
				<XeroIcon
					onClick={() => {
						if (xeroShortCode) {
							window.open(
								`https://go.xero.com/organisationlogin/default.aspx?shortcode=${xeroShortCode}&redirecturl=/AccountsReceivable/Edit.aspx?InvoiceID=${xeroId}`,
								'_blank'
							);
						} else {
							window.open(`https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${xeroId}`, '_blank');
						}
					}}
				/>
			</ForecastTooltip>
		);
	} else if (sageIntacctId) {
		return (
			<ForecastTooltip
				content={formatMessage({id: 'invoicing.open_invoice_in'}, {systemName: 'Sage Intacct'})}
				delay={[50, 100]}
				disabled={true} // Seems like ther is not simple way to construct the link... need to figure this out. For now it's disabled and it just shows the logo.
			>
				<SageIntacctIcon size={SageIntacctIcon.SIZE.MEDIUM} />
			</ForecastTooltip>
		);
	} else if (quickbooksId) {
		return (
			<ForecastTooltip
				content={formatMessage({id: 'invoicing.open_invoice_in'}, {systemName: 'QuickBooks'})}
				delay={[50, 100]}
			>
				<QuickbooksIcon
					onClick={() => {
						window.open(
							`${process.env.QUICKBOOKS_BASE_URL}/app/invoice?deeplinkcompanyid=${
								quickbooksRealmId ? quickbooksRealmId : company.quickbooksRealmId
							}&txnId=${quickbooksId}`,
							'_blank'
						);
					}}
					size={QuickbooksIcon.SIZE.STANDARD}
				/>
			</ForecastTooltip>
		);
	} else if (economicId) {
		return (
			<ForecastTooltip
				content={formatMessage({id: 'invoicing.open_invoice_in'}, {systemName: 'E-conomic'})}
				delay={[50, 100]}
			>
				<EconomicIcon
					onClick={() => {
						if (status === 'DRAFT' || status === 'EXPORTED') {
							window.open(`https://secure.e-conomic.com/sales/invoicing/invoices`, '_blank');
						} else if (status === 'APPROVED') {
							window.open(`https://secure.e-conomic.com/sales/invoicing/archive`, '_blank');
						} else if (status === 'SENT') {
							window.open(`https://secure.e-conomic.com/sales/invoicing/sent`, '_blank');
						}
					}}
					size={EconomicIcon.SIZE.STANDARD}
				/>
			</ForecastTooltip>
		);
	} else {
		return (
			<Button emphasis={'low'} onPress={() => onExportClick(projectInvoice || invoice, company)}>
				{formatMessage({id: 'common.export'})}
			</Button>
		);
	}
};
export default createFragmentContainer(ExportInvoiceButton, {
	project: graphql`
		fragment ExportInvoiceButton_project on ProjectType {
			id
			invoices {
				edges {
					node {
						id
						xeroId
						xeroTenantId
						quickbooksId
						economicId
						sageIntacctId
						quickbooksRealmId
						status
					}
				}
			}
		}
	`,
	company: graphql`
		fragment ExportInvoiceButton_company on Company
		@argumentDefinitions(
			xeroInvoicingImprovements: {type: "Boolean!", defaultValue: false}
			qboInvoicingImprovements: {type: "Boolean!", defaultValue: false}
		) {
			newXeroEnabled
			quickbooksEnabled
			economicEnabled
			quickbooksRealmId
			sageIntacctConnectionEnabled
			clients {
				edges {
					node {
						id
						name
						xeroContactId
						# Requested only with the Xero improvements flag on: flag-off pages send today's query (D8).
						xeroDefaultTaxRate @include(if: $xeroInvoicingImprovements) {
							name
							effectiveRate
							organisation
							externalId
						}
						economicCustomerId
						quickbooksCustomerId
						quickbooksRealmId
						qboDefaultTaxRate @include(if: $qboInvoicingImprovements) {
							name
							effectiveRate
							organisation
						}
						sageIntacctCustomerId
					}
				}
			}
		}
	`,
});
